import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI, schoolsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Switch } from '../../components/ui/switch';
import {
  HeartPulse, BookOpen, Pill, Plus, Trash2, CheckCircle, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * ParentCare — Medications, classes, and daily confirmations.
 * Pilot scope: active medications + classes/extracurriculars + today's status.
 *
 * Answers:
 * - Did my child get their medication today?
 * - What classes are active?
 * - Was the class/medication confirmed?
 */
export const ParentCare = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const familyId = user?.familyId || searchParams.get('family_id') || localStorage.getItem('family_id');

  // Children (for selectors)
  const [children, setChildren] = useState([]);

  // Medications
  const [medications, setMedications] = useState([]);
  const [loadingMeds, setLoadingMeds] = useState(true);
  const [showMedForm, setShowMedForm] = useState(false);
  const [savingMed, setSavingMed] = useState(false);
  const [newMed, setNewMed] = useState({
    child_id: '', medication_name: '', dose: '', instructions: '',
    times: [''], start_date: '', end_date: '', caregiver_responsible: true,
  });

  // Classes
  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [showClassForm, setShowClassForm] = useState(false);
  const [savingClass, setSavingClass] = useState(false);
  const [newClass, setNewClass] = useState({
    child_id: '', name: '', category: 'other', days_of_week: [],
    start_time: '', end_time: '', location: '', instructor: '',
    caregiver_transport: false, notes: '',
  });

  useEffect(() => {
    if (familyId) {
      loadChildren();
      loadMedications();
      loadClasses();
    }
  }, [familyId]);

  const loadChildren = async () => {
    try {
      const res = await schoolsAPI.getChildrenWithSchools(familyId);
      if (res.data?.ok) setChildren(res.data.children || []);
    } catch (err) {
      console.error('Failed to load children:', err);
    }
  };

  const loadMedications = async () => {
    try {
      const res = await adminAPI.getMedications(familyId);
      if (res.data?.ok) setMedications(res.data.medications || []);
    } catch (err) {
      console.error('Failed to load medications:', err);
    } finally {
      setLoadingMeds(false);
    }
  };

  const loadClasses = async () => {
    try {
      const res = await adminAPI.getClasses(familyId);
      if (res.data?.ok) setClasses(res.data.classes || []);
    } catch (err) {
      console.error('Failed to load classes:', err);
    } finally {
      setLoadingClasses(false);
    }
  };

  // --- Medication handlers ---

  const handleSaveMedication = async () => {
    const times = newMed.times.filter(t => t.trim());
    if (!newMed.child_id || !newMed.medication_name || !newMed.dose || times.length === 0) {
      toast.error('Please fill in name, child, dose, and at least one time');
      return;
    }
    setSavingMed(true);
    try {
      const payload = { ...newMed, times };
      const res = await adminAPI.createMedication(familyId, payload);
      if (res.data?.ok) {
        toast.success('Medication added');
        setMedications(prev => [...prev, res.data.medication]);
        setShowMedForm(false);
        setNewMed({ child_id: '', medication_name: '', dose: '', instructions: '', times: [''], start_date: '', end_date: '', caregiver_responsible: true });
      } else {
        toast.error(res.data?.error || 'Failed to save medication');
      }
    } catch (err) {
      toast.error('Failed to save medication');
    } finally {
      setSavingMed(false);
    }
  };

  const handleDeleteMedication = async (medId) => {
    try {
      await adminAPI.deleteMedication(familyId, medId);
      setMedications(prev => prev.filter(m => m.id !== medId));
      toast.success('Medication removed');
    } catch (err) {
      toast.error('Failed to remove medication');
    }
  };

  // --- Class handlers ---

  const handleSaveClass = async () => {
    if (!newClass.child_id || !newClass.name || !newClass.start_time || newClass.days_of_week.length === 0) {
      toast.error('Please fill in name, child, day(s), and start time');
      return;
    }
    setSavingClass(true);
    try {
      const res = await adminAPI.createClass(familyId, newClass);
      if (res.data?.ok) {
        toast.success('Class added');
        setClasses(prev => [...prev, res.data.class]);
        setShowClassForm(false);
        setNewClass({ child_id: '', name: '', category: 'other', days_of_week: [], start_time: '', end_time: '', location: '', instructor: '', caregiver_transport: false, notes: '' });
      } else {
        toast.error(res.data?.error || 'Failed to save class');
      }
    } catch (err) {
      toast.error('Failed to save class');
    } finally {
      setSavingClass(false);
    }
  };

  const handleDeleteClass = async (classId) => {
    try {
      await adminAPI.deleteClass(familyId, classId);
      setClasses(prev => prev.filter(c => c.id !== classId));
      toast.success('Class removed');
    } catch (err) {
      toast.error('Failed to remove class');
    }
  };

  // --- Helpers ---

  const todayDow = new Date().getDay(); // 0=Sun, convert to Mon=0
  const todayIdx = todayDow === 0 ? 6 : todayDow - 1;

  const getTodayMedStatus = (med) => {
    // For pilot: show scheduled times. Real confirmation tracking comes in Tier B.
    const times = med.times || [];
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const past = times.filter(t => t.slice(0, 5) <= currentTime);
    const upcoming = times.filter(t => t.slice(0, 5) > currentTime);
    return { past, upcoming, total: times.length };
  };

  const isClassToday = (cls) => {
    return (cls.days_of_week || []).includes(todayIdx);
  };

  const loading = loadingMeds || loadingClasses;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="parent-care">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
            <HeartPulse className="w-5 h-5 text-rose-500" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Care</h1>
            <p className="text-sm text-slate-500">Medications, classes & daily status</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-slate-200 border-t-primary rounded-full mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading care details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-care">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
          <HeartPulse className="w-5 h-5 text-rose-500" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Care</h1>
          <p className="text-sm text-slate-500">Medications, classes & daily status</p>
        </div>
      </div>

      {/* ============ MEDICATIONS ============ */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="medications-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
              <Pill className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800">Medications</h3>
              <p className="text-xs text-slate-500">Caregiver is reminded at dose time</p>
            </div>
          </div>
          <button
            onClick={() => setShowMedForm(v => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
            data-testid="add-medication-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Add medication
          </button>
        </div>

        {/* Add medication form */}
        {showMedForm && (
          <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
            <p className="text-xs font-medium text-slate-600 mb-1">New medication</p>

            <select
              value={newMed.child_id}
              onChange={e => setNewMed(p => ({ ...p, child_id: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select child...</option>
              {children.map(c => (
                <option key={c.id} value={c.id}>{c.first_name}</option>
              ))}
            </select>

            <div className="grid grid-cols-2 gap-2">
              <input
                placeholder="Medication name"
                value={newMed.medication_name}
                onChange={e => setNewMed(p => ({ ...p, medication_name: e.target.value }))}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
              <input
                placeholder="Dose (e.g. 5ml, 1 tablet)"
                value={newMed.dose}
                onChange={e => setNewMed(p => ({ ...p, dose: e.target.value }))}
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>

            <input
              placeholder="Instructions (e.g. With food, before sleep)"
              value={newMed.instructions}
              onChange={e => setNewMed(p => ({ ...p, instructions: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />

            {/* Dose times */}
            <div>
              <p className="text-xs text-slate-500 mb-1.5">Dose time(s)</p>
              <div className="space-y-2">
                {newMed.times.map((t, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input type="time" value={t}
                      onChange={e => setNewMed(p => {
                        const times = [...p.times];
                        times[i] = e.target.value;
                        return { ...p, times };
                      })}
                      className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
                    />
                    {newMed.times.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setNewMed(p => ({ ...p, times: p.times.filter((_, j) => j !== i) }))}
                        className="p-1.5 text-slate-400 hover:text-rose-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {newMed.times.length < 4 && (
                  <button
                    type="button"
                    onClick={() => setNewMed(p => ({ ...p, times: [...p.times, ''] }))}
                    className="text-xs text-primary hover:underline"
                  >
                    + Add another time
                  </button>
                )}
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Start date</label>
                <input type="date" value={newMed.start_date}
                  onChange={e => setNewMed(p => ({ ...p, start_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">End date (leave blank if ongoing)</label>
                <input type="date" value={newMed.end_date}
                  onChange={e => setNewMed(p => ({ ...p, end_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg text-xs text-amber-700">
              Kivo reminds your caregiver and logs their confirmation. Always consult your doctor for medical decisions.
            </div>

            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSaveMedication}
                disabled={savingMed}
                className="flex-1 bg-primary text-white text-sm font-medium py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {savingMed ? 'Saving...' : 'Add medication'}
              </button>
              <button
                onClick={() => setShowMedForm(false)}
                className="px-4 text-sm text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Medication list with today's status */}
        {medications.length > 0 ? (
          <div className="space-y-2">
            {medications.map(med => {
              const status = getTodayMedStatus(med);
              return (
                <div key={med.id} className="p-3 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-800">
                        {med.medication_name}
                        <span className="ml-2 text-xs text-slate-500 font-normal">{med.dose}</span>
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {(med.times || []).map(t => t.slice(0, 5)).join(', ')}
                        {med.end_date ? ` · until ${med.end_date}` : ' · ongoing'}
                      </p>
                      {med.instructions && (
                        <p className="text-xs text-slate-400 mt-0.5">{med.instructions}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteMedication(med.id)}
                      className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Today's dose status */}
                  {status.total > 0 && (
                    <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100">
                      {status.past.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle className="w-3.5 h-3.5" />
                          {status.past.length} dose{status.past.length > 1 ? 's' : ''} due
                        </span>
                      )}
                      {status.upcoming.length > 0 && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3.5 h-3.5" />
                          {status.upcoming.length} upcoming ({status.upcoming[0].slice(0, 5)})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 bg-slate-50 rounded-xl text-center">
            <Pill className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No medications added</p>
            <p className="text-xs text-slate-400 mt-1">Add antibiotics, vitamins, or any recurring medication</p>
          </div>
        )}
      </div>

      {/* ============ CLASSES & EXTRACURRICULARS ============ */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="classes-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-800">Classes & Activities</h3>
              <p className="text-xs text-slate-500">Kivo reminds the caregiver 60 min before class</p>
            </div>
          </div>
          <button
            onClick={() => setShowClassForm(v => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
            data-testid="add-class-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Add class
          </button>
        </div>

        {/* Add class form */}
        {showClassForm && (
          <div className="bg-slate-50 rounded-xl p-4 mb-4 space-y-3">
            <p className="text-xs font-medium text-slate-600 mb-1">New class</p>

            <select
              value={newClass.child_id}
              onChange={e => setNewClass(p => ({ ...p, child_id: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select child...</option>
              {children.map(c => (
                <option key={c.id} value={c.id}>{c.first_name}</option>
              ))}
            </select>

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Class name</label>
              <input
                placeholder="e.g. Swimming, Piano, Kumon Math"
                value={newClass.name}
                onChange={e => setNewClass(p => ({ ...p, name: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Category</label>
              <select
                value={newClass.category}
                onChange={e => setNewClass(p => ({ ...p, category: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              >
                {['sport', 'music', 'art', 'academic', 'other'].map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>

            {/* Days */}
            <div>
              <p className="text-xs text-slate-500 mb-1.5">Days</p>
              <div className="flex gap-1.5 flex-wrap">
                {DAY_LABELS.map((label, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setNewClass(p => ({
                      ...p,
                      days_of_week: p.days_of_week.includes(idx)
                        ? p.days_of_week.filter(d => d !== idx)
                        : [...p.days_of_week, idx]
                    }))}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                      newClass.days_of_week.includes(idx)
                        ? 'bg-primary text-white'
                        : 'bg-white border border-slate-200 text-slate-600'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time + location */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Start time</label>
                <input type="time" value={newClass.start_time}
                  onChange={e => setNewClass(p => ({ ...p, start_time: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">End time (optional)</label>
                <input type="time" value={newClass.end_time}
                  onChange={e => setNewClass(p => ({ ...p, end_time: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <input
              placeholder="Location (optional)"
              value={newClass.location}
              onChange={e => setNewClass(p => ({ ...p, location: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            />

            {/* Caregiver transport toggle */}
            <div className="flex items-center justify-between p-2.5 bg-white rounded-lg border border-slate-100">
              <div>
                <p className="text-sm font-medium text-slate-700">Caregiver drops off / picks up</p>
                <p className="text-xs text-slate-400">Nanny needs to take or collect the child</p>
              </div>
              <Switch
                checked={newClass.caregiver_transport}
                onCheckedChange={v => setNewClass(p => ({ ...p, caregiver_transport: v }))}
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button
                onClick={handleSaveClass}
                disabled={savingClass}
                className="flex-1 bg-primary text-white text-sm font-medium py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {savingClass ? 'Saving...' : 'Add class'}
              </button>
              <button
                onClick={() => setShowClassForm(false)}
                className="px-4 text-sm text-slate-500 hover:text-slate-700"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Class list with today highlight */}
        {classes.length > 0 ? (
          <div className="space-y-2">
            {classes.map(cls => {
              const today = isClassToday(cls);
              return (
                <div key={cls.id} className={`flex items-center justify-between p-3 rounded-xl ${
                  today ? 'bg-purple-50 border border-purple-100' : 'bg-slate-50'
                }`}>
                  <div>
                    <p className="text-sm font-medium text-slate-800">
                      {cls.name}
                      <span className="ml-2 text-xs px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded font-medium capitalize">
                        {cls.category}
                      </span>
                      {today && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 bg-purple-200 text-purple-700 rounded font-medium">
                          Today
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {(cls.days_of_week || []).map(d => DAY_LABELS[d]).join(', ')} · {cls.start_time?.slice(0, 5)}
                      {cls.location ? ` · ${cls.location}` : ''}
                    </p>
                    {cls.caregiver_transport && (
                      <p className="text-xs text-amber-600 mt-0.5">Caregiver transport needed</p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteClass(cls.id)}
                    className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-6 bg-slate-50 rounded-xl text-center">
            <BookOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No classes added yet</p>
            <p className="text-xs text-slate-400 mt-1">Add swimming, phonics, dance, football...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentCare;
